import { IAudio, IDraftAudio, IDraftImage, IGroupSet, IImage, ITask, ITaskGroup } from '../../types/tasks.types'
import http, { handleHttpError, handleHttpResponse } from '../../shared/http'
import tasksUtils from './tasks.utils'
import { ITextOption } from '../../screens/Sandbox/sandbox.types'
import { deepCopy } from '../../utils/misc.utils'
import { HTTPResponse } from '../../shared/http/http.types'
import measureUtils from '../../utils/measureUtils'

const getGroupsTasks = async (groups: Array<ITaskGroup['id']>): Promise<ITask[]> => {
  const chunkSize = Math.ceil(groups.length / 32)

  const results: ITask[] = []

  for (let i = 0; i < 32; ++i) {
    const groupIds = groups.slice(i * chunkSize, chunkSize * (i + 1))
    const resp = await http
      .get(
        `/api/v1/admin/exercise_tasks/?group__in=${groupIds.join(
          ','
        )}&limit=10000&expand=answer_options,audio,images,word_to_match`
      )
      .then((r) => r.data.results)

    await new Promise((res) => setTimeout(() => res(true), 500))
    results.push(...resp)
  }

  return results
}

const getGroupsTasksWithSetIds = async (sets: Array<IGroupSet['id']>): Promise<ITask[]> => {
  const results: ITask[] = []

  for (let i = 0; i < sets.length; ++i) {
    const resp: { results: ITask[]; count: number; next: string } = await http
      .get(
        `/api/v1/admin/exercise_tasks/?group__set_id=${sets[i]}&limit=10000&expand=answer_options,audio,images,word_to_match`
      )
      .then((r) => r.data)
    results.push(...resp.results)
  }

  return results
}

const deleteTask = async (id: ITask['id']) => {
  return http.delete(`/api/v1/admin/exercise_tasks/${id}/`).then(handleHttpResponse).catch(handleHttpError)
}

const deleteTaskImage = async (id: IImage['id']): Promise<IImage> => {
  return http.delete(`/api/v1/admin/exercise_task_images/${id}/`).then((r) => r.data)
}

const addTaskImage = (taskId: ITask['id'], image: IDraftImage): Promise<IImage> => {
  const formData = new FormData()
  formData.set('task', taskId)
  formData.set('file', image.imageFile)
  formData.set('rightAnswer', image.rightAnswer ? 'true' : 'false')
  formData.set('isMain', image.isMain ? 'true' : 'false')

  if (image.scale) {
    formData.set('scale', (parseInt(image.scale.toString()) / 100).toString())
  } else {
    formData.set('scale', '0.4')
  }

  return http.post('/api/v1/admin/exercise_task_images/', formData).then((r) => r.data)
}

const patchImage = (id: IImage['id'], diff: Partial<IImage>) => {
  return http.patch(`/api/v1/admin/exercise_task_images/${id}/`, diff).then(handleHttpResponse).catch(handleHttpError)
}

const addTaskAudio = (taskId: ITask['id'], audio: File | IDraftAudio): Promise<IAudio> => {
  const formData = new FormData()
  formData.set('task', taskId)
  formData.set('file', 'rightAnswer' in audio ? (audio.file as File) : (audio as File))
  formData.set('rightAnswer', 'rightAnswer' in audio ? audio.rightAnswer.toString() : 'false')
  formData.set('type', 'type' in audio ? audio.type : 'TASK_DESCRIPTION')

  return http.post('/api/v1/admin/exercise_task_audio/', formData).then((r) => r.data)
}

const deleteTaskAudio = (id: IAudio['id']): Promise<IAudio> => {
  return http.delete(`/api/v1/admin/exercise_task_audio/${id}/`).then((r) => r.data)
}

const addTaskTextOption = (option: Omit<ITextOption, 'id'>): Promise<ITextOption> => {
  option.approximatedTextWidth = measureUtils.getTextWidth(option.text, '48px Chitalka')

  return http.post('/api/v1/admin/exercise_answer_options/', option).then((r) => r.data)
}

const updateTaskTextOption = (option: ITextOption): Promise<ITextOption> => {
  option.approximatedTextWidth = measureUtils.getTextWidth(option.text, '48px Chitalka')

  return http.patch('/api/v1/admin/exercise_answer_options/' + option.id + '/', option).then((r) => r.data)
}

const removeTaskTextOption = (id: ITextOption['id']) => {
  return http.delete(`/api/v1/admin/exercise_answer_options/${id}/`).then((r) => r.data)
}

const serveTaskAssertsDiff = async (updated: ITask, prev: ITask) => {
  const diff = tasksUtils.computeTasksDiff(updated, prev)

  const removeRequests: Promise<any>[] = []
  const createdOptions = []
  const updatedOptions = []
  const createdAudio = []
  const createdImages = []

  for (const option of diff.answerOptions.removed) removeRequests.push(removeTaskTextOption(option))
  for (const option of diff.answerOptions.created) {
    createdOptions.push(addTaskTextOption({ ...option, task: updated.id }))
  }
  for (const option of updated.answerOptions) {
    const prevOption = prev.answerOptions.find((o) => o.id === option.id)
    if (!prevOption) continue

    if (prevOption.text !== option.text || prevOption.rightAnswer !== option.rightAnswer) {
      updatedOptions.push(updateTaskTextOption(option))
    }
  }
  for (const image of diff.images.removed) removeRequests.push(deleteTaskImage(image))
  for (const taskAudio of diff.taskAudio.removed) removeRequests.push(deleteTaskAudio(taskAudio))
  await Promise.all(removeRequests)

  for (const image of diff.images.created) createdImages.push(addTaskImage(prev.id, image))
  for (const audio of diff.audio.created) createdAudio.push(addTaskAudio(prev.id, audio))
  for (const taskAudio of diff.taskAudio.created) createdAudio.push(addTaskAudio(prev.id, taskAudio))

  // @ts-ignore
  const patchedScaleImages = updated.images.filter(
    (i) =>
      // @ts-ignore
      prev.images.find((ui) => ui.id === i.id)?.scale !== i.scale
  )

  // @ts-ignore
  const patchedOrderingIndexImages = updated.images.filter(
    (i) =>
      // @ts-ignore
      prev.images.find((ui) => ui.id === i.id)?.orderingIndex !== i.orderingIndex
  )

  const scaleImagesRequests = patchedScaleImages.map((img) => patchImage(img.id, { scale: img.scale / 100 }))
  const indexImagesRequests = patchedOrderingIndexImages.map((img) =>
    patchImage(img.id, { orderingIndex: img.orderingIndex })
  )
  const patchedImagesResp: Array<HTTPResponse<IImage>> = await Promise.all([
    ...scaleImagesRequests,
    ...indexImagesRequests,
  ])

  const images = await Promise.all(createdImages)
  const audio = await Promise.all(createdAudio)

  const options = await Promise.all(createdOptions)
  const updOptions = await Promise.all(updatedOptions)
  const finalOptions = [...updOptions, ...options]

  for (const option of prev.answerOptions) {
    if (!finalOptions.some((o) => o.id === option.id)) {
      finalOptions.unshift(option)
    }
  }

  const updatedImages = deepCopy(
    [...prev.images, ...images].filter((img) => !diff.images.removed.some((i) => i === img.id))
  )

  const updatedAudio = deepCopy(
    [...prev.audio, ...audio].filter((ad) => !diff.taskAudio.removed.some((i) => i === ad.id))
  )

  for (const img of patchedImagesResp) {
    if (img.status === 'success') {
      const ind = updatedImages.findIndex((i) => i.id === img.body.id)
      if (ind !== -1) {
        updatedImages[ind].scale = img.body.scale
        updatedImages[ind].orderingIndex = img.body.orderingIndex
      }
    }
  }

  return {
    images: updatedImages,
    audio: updatedAudio,
    options: finalOptions.filter((opt) => !diff.answerOptions.removed.some((o) => o === opt.id)),
  }
}

const updateTask = async (updated: ITask, prev: ITask): Promise<ITask> => {
  const { images, audio, options } = await serveTaskAssertsDiff(updated, prev)

  const updates: Partial<ITask> = { type: updated.type }
  if (updated.wordToMatch && updated.wordToMatch.text !== prev.wordToMatch?.text) {
    const option = await addTaskTextOption({ task: prev.id, text: updated.wordToMatch.text, rightAnswer: false })
    updates.wordToMatch = option.id as any
  }
  if (updated.question) updates.question = updated.question
  if (updated.description) updates.description = updated.description

  await http.patch('/api/v1/admin/exercise_tasks/' + prev.id + '/', updates)

  return {
    ...prev,
    ...updated,
    audio,
    images,
    answerOptions: options,
    wordToMatch: updated.wordToMatch,
  }
}

const createEmptyTask = (groupId: string, type = 0, extraFields: Partial<ITask> = {}): Promise<ITask> => {
  return http
    .post('/api/v1/admin/exercise_tasks/', {
      group: groupId,
      type,
      answerOptions: [],
      images: [],
      audio: [],
      ...extraFields,
    })
    .then((r) => r.data)
}

const patchTask = (id: ITask['id'], updates: Partial<ITask>): Promise<ITask> => {
  return http.patch('/api/v1/admin/exercise_tasks/' + id + '/', updates).then((r) => r.data)
}

const tasksService = {
  createEmptyTask,
  getGroupsTasks,
  getGroupsTasksWithSetIds,
  updateTask,
  addTaskAudio,
  deleteTaskImage,
  addTaskImage,
  deleteTaskAudio,
  addTaskTextOption,
  removeTaskTextOption,
  patchTask,
  deleteTask,
}
export default tasksService
